import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { set } from 'ember-set-helper';
import _drop from 'lodash/drop';
import _take from 'lodash/take';
import BvAvatar from 'volta/components/bv-avatar';
import BvTooltip from 'volta/components/bv-tooltip';
import classNames from 'volta/helpers/class-names';
import { abbreviateNumber, userName } from 'volta/utils/text-utils';

import type User from 'volta/models/user';
import type UserProfile from 'volta/models/user-profile';
import type SessionUserService from 'volta/services/session-user';
import type { THueVariationString, TSize } from 'volta/utils/style-utils';

const SHOW_USERS_LIMIT = 2;

interface BvAvatarGroupSignature {
  Args: {
    size?: TSize;
    users: (UserProfile | User)[];
    colors?: Record<string, THueVariationString>;
  };
  Element: HTMLDivElement;
}

export default class BvAvatarGroupComponent extends Component<BvAvatarGroupSignature> {
  @service sessionUser!: SessionUserService;

  @tracked isHovered = false;

  get users() {
    return Array.isArray(this.args.users) ? this.args.users.slice() : [];
  }

  get colors() {
    const myUserId = this.sessionUser.userId;
    const defaultColors = myUserId ? { [myUserId]: 'indigo' } : {};
    return { ...defaultColors, ...(this.args.colors ?? {}) };
  }

  get showedUsers() {
    const { users } = this;
    return _take(
      users,
      users.length === SHOW_USERS_LIMIT + 1 ? SHOW_USERS_LIMIT + 1 : SHOW_USERS_LIMIT
    );
  }

  get hiddenUserNames() {
    const { users } = this;
    return _drop<User | UserProfile>(users, 2)
      .map((user) => userName(user.firstName, user.lastName, user.login) ?? '')
      .join(', ');
  }

  get showCount() {
    return this.users.length > SHOW_USERS_LIMIT + 1;
  }

  get count() {
    return abbreviateNumber(this.users.length - SHOW_USERS_LIMIT);
  }

  username = (user: User | UserProfile) =>
    userName(user.firstName, user.lastName, user.login) ?? '';

  getColor = (user: User | UserProfile) =>
    (user.isInactive
      ? 'gray-2'
      : ((user.id && this.colors[user.id]) ?? 'teal')) as THueVariationString;

  <template>
    <div
      class='bv-avatar-group'
      {{on 'mouseenter' (set this 'isHovered' true)}}
      {{on 'mouseleave' (set this 'isHovered' false)}}
      ...attributes
    >
      {{#let
        (classNames
          'bv-avatar-group__avatar-wrapper'
          (if this.isHovered 'bv-avatar-group__avatar-wrapper--hovered')
        )
        as |classNames|
      }}
        {{#each this.showedUsers key='id' as |user|}}
          <div class={{classNames}}>
            <BvAvatar
              @size={{@size}}
              @presence={{if user.isInactive 'inactive' (if user.connected 'online' 'offline')}}
              @firstName={{user.firstName}}
              @lastName={{user.lastName}}
              @login={{user.login}}
              @background={{this.getColor user}}
            />
            <BvTooltip @text={{this.username user}} />
          </div>
        {{/each}}
        {{#if this.showCount}}
          <div class={{classNames}}>
            <BvAvatar @size={{@size}} @text={{concat '+' this.count}} />
            <BvTooltip @text={{this.hiddenUserNames}} />
          </div>
        {{/if}}
      {{/let}}
    </div>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvAvatarGroup: typeof BvAvatarGroupComponent;
    'bv-avatar-group': typeof BvAvatarGroupComponent;
  }
}
